import { useMemo } from 'react';

import { useT } from '@/hooks';
import { EventMarket, EventOutcome, EventResponse } from '@/types';
import {
  findOutcomeIndexForTeam,
  getCanonicalMarketLine,
  getOutcomes,
  getSpreadLine,
  resolveDrawOutcome,
} from '@/utils';

interface UseGameOutcomesProps {
  event: EventResponse;
  activeMarket: EventMarket | undefined;
  isML: boolean;
  isTeamLabel: boolean;
  isSpread: boolean;
  isTotal: boolean;
  homeMarket: EventMarket | undefined;
  awayMarket: EventMarket | undefined;
  drawMarket: EventMarket | undefined;
  activeOutcomeId?: string;
  realtimePrices: any;
  formatSportPrice: (p: any) => string;
}

export const useGameOutcomes = ({
  event,
  activeMarket,
  isML,
  isTeamLabel,
  isSpread,
  isTotal,
  homeMarket,
  awayMarket,
  drawMarket,
  activeOutcomeId,
  realtimePrices,
  formatSportPrice,
}: UseGameOutcomesProps) => {
  const { t } = useT();

  return useMemo(() => {
    const getPriceData = (m: EventMarket | undefined, isAway: boolean, teamName?: string) => {
      if (!m) return { price: '', id: undefined, marketId: undefined };
      const outcomes = getOutcomes(m);
      const teamIdx = findOutcomeIndexForTeam(outcomes, teamName);

      const isIndependent = !!awayMarket;
      let outcome: EventOutcome | undefined;

      if (isIndependent) {
        outcome = outcomes.find((o) => o.name?.toLowerCase() === 'yes') || outcomes[0];
      } else {
        outcome = teamIdx !== -1 ? outcomes[teamIdx] : outcomes[isAway ? 1 : 0];
      }

      const clobIds = m.clobTokenIds
        ? typeof m.clobTokenIds === 'string'
          ? JSON.parse(m.clobTokenIds)
          : m.clobTokenIds
        : [];
      const outcomeIndex = outcomes.indexOf(outcome as any);
      const tokenId = outcomeIndex !== -1 ? clobIds[outcomeIndex] : null;

      const pStatic = outcome?.bestAsk || outcome?.price;
      const p =
        realtimePrices && tokenId ? (realtimePrices[tokenId]?.askPrice ?? pStatic) : pStatic;

      return {
        price: formatSportPrice(p),
        id: outcome?.id,
        marketId: m.id,
      };
    };

    const homeTeam = event.homeTeam;
    const awayTeam = event.awayTeam;
    const teamMarket = isML ? homeMarket : activeMarket;
    const hData = getPriceData(teamMarket, false, homeTeam?.name);
    const aData = getPriceData(
      isML ? awayMarket || homeMarket : activeMarket,
      true,
      awayTeam?.name
    );

    const isActive = (id?: string) => !!activeOutcomeId && id === activeOutcomeId;

    if (isTeamLabel) {
      let draw: any = null;
      if (isML) {
        const { pricing } = resolveDrawOutcome(event);
        if (pricing?.id) {
          draw = {
            label: t('sports.draw', { defaultValue: 'Draw' }),
            price: pricing.price,
            id: pricing.id,
            active: isActive(pricing.id),
            marketId: pricing.marketId,
            color: '#475569',
          };
        }
      }

      return {
        team1: {
          label: homeTeam?.abbreviation?.toUpperCase() || 'HME',
          color: homeTeam?.color || '#2563eb',
          price: hData.price,
          line: isSpread ? getSpreadLine(activeMarket, homeTeam?.name, false) : undefined,
          id: hData.id,
          active: isActive(hData.id),
          marketId: hData.marketId,
        },
        team2: {
          label: awayTeam?.abbreviation?.toUpperCase() || 'AWY',
          color: awayTeam?.color || '#dc2626',
          price: aData.price,
          line: isSpread ? getSpreadLine(activeMarket, awayTeam?.name, true) : undefined,
          id: aData.id,
          active: isActive(aData.id),
          marketId: aData.marketId,
        },
        draw,
      };
    }

    if (isTotal) {
      const line = getCanonicalMarketLine(activeMarket);
      return {
        team1: {
          label: 'O',
          color: '#359a5e',
          price: hData.price,
          line,
          id: hData.id,
          active: isActive(hData.id),
          marketId: hData.marketId,
        },
        team2: {
          label: 'U',
          color: '#cb3131',
          price: aData.price,
          line,
          id: aData.id,
          active: isActive(aData.id),
          marketId: aData.marketId,
        },
      };
    }

    const outcomes = getOutcomes(activeMarket);
    return {
      team1: {
        label: outcomes[0]?.name?.substring(0, 3).toUpperCase() || 'YES',
        price: hData.price,
        id: hData.id,
        active: isActive(hData.id),
        marketId: hData.marketId,
      },
      team2: {
        label: outcomes[1]?.name?.substring(0, 3).toUpperCase() || 'NO',
        price: aData.price,
        id: aData.id,
        active: isActive(aData.id),
        marketId: aData.marketId,
      },
    };
  }, [
    activeMarket,
    activeOutcomeId,
    isML,
    isTeamLabel,
    isSpread,
    isTotal,
    homeMarket,
    awayMarket,
    drawMarket,
    realtimePrices,
    event,
    formatSportPrice,
    t,
  ]);
};
